import { useEffect, useRef, useState } from 'react';
import { api, type Brand, type Channel, type Room, type RoomGuest, type RoomComment, type Asset, type GuestMove, type BannerPos, type BannerStyle, type DoorLink, type ChatRead, type RoomPoll, type PlanSegment } from '../lib/api';
import { joinMedia, mount, sendToRoom, shareScreen, type Seat } from '../lib/media';
import { BANNER_POSITIONS, BANNER_STYLES, bannerBox, encodeStageMessage, isSuperChat, money, pollShares, queueOrder, reactionBurst, type StageMessage } from '../lib/live-chat';
import { displayName as assetName } from './AssetGrid';
import { livePreflight } from '../lib/preflight-live';
import { LiveFromTheDoor } from './LiveFromTheDoor';
import { RoomLook } from './lab/RoomLook';
import { showClock, planTotal, mmss } from '../lib/live-plan';
import type { Room as MediaRoom } from 'livekit-client';

/**
 * Live: the host's console (G1, G3). A room is a show's door — a join link a
 * guest opens with nothing but a name. The host sees who is waiting, admits,
 * holds, puts on air or removes. The media itself (camera and sound between
 * everyone) joins when the media server is configured; the door works either
 * way, so a show can be run with a guest on a phone call today and on camera
 * the day the server is there.
 */
const TONE: Record<RoomGuest['status'], string> = {
  waiting: 'var(--blue)', held: 'var(--ink-3)', admitted: 'var(--green)', on_air: 'var(--red)', removed: 'var(--ink-3)', left: 'var(--ink-3)', moderator: 'var(--accent)',
};
const WORD: Record<RoomGuest['status'], string> = {
  waiting: 'waiting', held: 'on hold', admitted: 'in the room', on_air: 'ON AIR', removed: 'removed', left: 'left', moderator: 'moderator',
};

export function Live({ brand, onOpenCard }: { brand: Brand | null; onOpenCard?: (id: string) => void }) {
  const [rooms, setRooms] = useState<Room[]>([]);
  const [media, setMedia] = useState(false);
  const [channels, setChannels] = useState<Channel[]>([]);
  const [open, setOpen] = useState<string | null>(null);
  const [guests, setGuests] = useState<RoomGuest[]>([]);
  const [name, setName] = useState('');
  const [channelId, setChannelId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const alive = useRef(true);
  const mediaRoom = useRef<MediaRoom | null>(null);
  const [seats, setSeats] = useState<Seat[]>([]);
  const [joined, setJoined] = useState(false);
  const me = useRef<HTMLVideoElement>(null);
  // G10–G12: the comment queue, the control room's settings, the pictures the door can wear.
  const [comments, setComments] = useState<RoomComment[]>([]);
  const [images, setImages] = useState<Asset[]>([]);
  const [form, setForm] = useState<{ description: string; rules: string; donateUrl: string; donateLabel: string; superchat: string; accent: string; welcome: string; nextAt: string; links: DoorLink[] }>({ description: '', rules: '', donateUrl: '', donateLabel: '', superchat: '5', accent: '', welcome: '', nextAt: '', links: [] });
  const onStage = useRef<string | null>(null);
  const [line, setLine] = useState('');
  const [read, setRead] = useState<ChatRead | null>(null);
  const [screenOn, setScreenOn] = useState(false);
  const [debriefing, setDebriefing] = useState(false);
  // G16: polls — the list with tallies, the form, and which one is on the screen.
  const [polls, setPolls] = useState<RoomPoll[]>([]);
  const [pollForm, setPollForm] = useState<{ question: string; options: string[] }>({ question: '', options: ['', ''] });
  const [pollOnScreen, setPollOnScreen] = useState<string | null>(null);
  const pollSent = useRef<string>('');
  // G17: the room's reactions in the last few seconds — shown here, sent to the stage as they change.
  const [reactions, setReactions] = useState<Record<string, number>>({});
  // D20: the run of show — the plan edited in place, the clock ticking every second.
  const [planForm, setPlanForm] = useState<PlanSegment[]>([]);
  const [planMinutes, setPlanMinutes] = useState('30');
  const [drafting, setDrafting] = useState(false);
  const [now, setNow] = useState(() => new Date());
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t); }, []);
  const reactSeen = useRef<Record<string, number>>({});
  const [debriefOpen, setDebriefOpen] = useState(false);
  const [reading, setReading] = useState(false);
  const [dragXY, setDragXY] = useState<[number, number] | null>(null);
  const miniStage = useRef<HTMLDivElement>(null);

  async function load() {
    try {
      const [r, c] = await Promise.all([api.rooms(), api.channels().catch(() => [] as Channel[])]);
      setRooms(r.rooms); setMedia(r.media); setChannels(c);
      setOpen((cur) => cur && r.rooms.some((x) => x.id === cur) ? cur : r.rooms[0]?.id ?? null);
      setError(null);
    } catch (e) { setError((e as Error).message); }
  }
  useEffect(() => { alive.current = true; void load(); return () => { alive.current = false; }; }, []);
  // One pulse every two seconds carries the guests, the comments, the polls and the room's feeling.
  useEffect(() => {
    if (!open) { setGuests([]); setComments([]); setPolls([]); setPollOnScreen(null); setReactions({}); return; }
    let live = true;
    const tick = () => api.roomPulse(open).then((p) => {
      if (!live) return;
      setGuests(p.guests); setComments(p.comments); setPolls(p.polls); setReactions(p.reactions);
      const b = reactionBurst(reactSeen.current, p.reactions); reactSeen.current = b.seen;
      if (b.float.length && mediaRoom.current) void sendToRoom(mediaRoom.current, encodeStageMessage({ type: 'react', counts: p.reactions })).catch(() => undefined);
    }).catch(() => undefined);
    void tick();
    // Same as the door: a console in a background tab pulses every 30 s, not
    // every 2 s, so an open tab does not keep the API's instance awake all day.
    const every = () => (document.hidden ? 30_000 : 2000);
    let t = setInterval(tick, every());
    const onVisibility = () => { clearInterval(t); if (!document.hidden) void tick(); t = setInterval(tick, every()); };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { live = false; clearInterval(t); document.removeEventListener('visibilitychange', onVisibility); };
  }, [open]);

  const room = rooms.find((r) => r.id === open) ?? null;
  const link = room ? `${window.location.origin}/green/${room.slug}` : '';
  useEffect(() => {
    if (!room) return;
    setForm({ description: room.description ?? '', rules: room.rules ?? '', donateUrl: room.donate_url ?? '', donateLabel: room.donate_label ?? '',
              superchat: String((room.superchat_min ?? 500) / 100), accent: room.accent ?? '',
              welcome: room.welcome ?? '', nextAt: room.next_at ? localInput(room.next_at) : '', links: room.links ?? [] });
    setPlanForm(room.plan ?? []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room?.id]);
  useEffect(() => {
    api.assets(brand?.id ?? null).then((a) => setImages(a.filter((x) => x.kind === 'image' && x.status === 'ready'))).catch(() => setImages([]));
  }, [brand?.id]);

  async function saveRoom(input: Parameters<typeof api.updateRoom>[1]) {
    if (!room) return;
    try { const r = await api.updateRoom(room.id, input); setRooms((prev) => prev.map((x) => (x.id === r.id ? r : x))); }
    catch (e) { setError((e as Error).message); }
  }

  /** The stage shows one comment at a time; whoever showed it — host here, or a moderator at the door — the console keeps the stage in step. */
  const shownNow = queueOrder(comments.filter((c) => c.status === 'shown'), 0).sort((a, b) => (a.created_at < b.created_at ? 1 : -1))[0] ?? null;
  useEffect(() => {
    const id = shownNow?.id ?? null;
    if (id === onStage.current || !mediaRoom.current || !room) return;
    onStage.current = id;
    const msg: StageMessage = shownNow
      ? { type: 'comment', id: shownNow.id, name: shownNow.name, text: shownNow.text, amountCents: shownNow.amount_cents || undefined, pos: room.banner_pos ?? 'bottom', accent: room.accent ?? null, xy: roomXY(room), style: room.banner_style ?? 'card' }
      : { type: 'clear' };
    void sendToRoom(mediaRoom.current, encodeStageMessage(msg)).catch(() => undefined);
  }, [shownNow?.id, room?.banner_pos, room?.accent, room?.banner_x, room?.banner_y, room?.banner_style, joined]);
  // a moved or restyled banner reaches the stage at once, not with the next comment
  useEffect(() => { onStage.current = null; }, [room?.banner_pos, room?.banner_x, room?.banner_y, room?.banner_style]);

  /** G14: the host drags the banner on a small stage; where it lands is a point in percent, saved on release. */
  function dragBanner(e: React.PointerEvent<HTMLDivElement>) {
    const box = miniStage.current; if (!box || !room) return;
    const r = box.getBoundingClientRect();
    const at = (ev: { clientX: number; clientY: number }): [number, number] => [
      Math.max(0, Math.min(100, ((ev.clientX - r.left) / r.width) * 100 - 4)),
      Math.max(0, Math.min(100, ((ev.clientY - r.top) / r.height) * 100 - 4)),
    ];
    setDragXY(at(e));
    const move = (ev: PointerEvent) => setDragXY(at(ev));
    const up = (ev: PointerEvent) => {
      window.removeEventListener('pointermove', move); window.removeEventListener('pointerup', up);
      const xy = at(ev); setDragXY(null);
      void saveRoom({ bannerXY: [Math.round(xy[0] * 100) / 100, Math.round(xy[1] * 100) / 100] });
    };
    window.addEventListener('pointermove', move); window.addEventListener('pointerup', up);
  }

  /** Links save once every row is whole; a half-typed row waits. */
  async function saveLinks(links: DoorLink[]) {
    const whole = links.filter((l) => l.label.trim() && l.url.trim());
    if (whole.some((l) => !/^https:\/\//.test(l.url.trim()))) { setError('every link on the page must start with https://'); return; }
    if (whole.length !== links.length) return;
    await saveRoom({ links: whole.map((l) => ({ label: l.label.trim(), url: l.url.trim() })) });
  }


  /** G16: the poll on the screen follows its tallies — every change goes to the stage, once. */
  const shownPoll = polls.find((p) => p.id === pollOnScreen) ?? null;
  useEffect(() => {
    if (!mediaRoom.current || !room) return;
    const key = shownPoll ? `${shownPoll.id}:${shownPoll.status}:${shownPoll.tallies.join(',')}:${room.banner_pos}:${room.banner_x}:${room.banner_y}` : 'none';
    if (key === pollSent.current) return;
    pollSent.current = key;
    const msg: StageMessage = shownPoll
      ? { type: 'poll', id: shownPoll.id, question: shownPoll.question, options: shownPoll.options, tallies: shownPoll.tallies, closed: shownPoll.status === 'closed', pos: room.banner_pos ?? 'top-right', accent: room.accent ?? null, xy: roomXY(room) }
      : { type: 'poll-clear' };
    void sendToRoom(mediaRoom.current, encodeStageMessage(msg)).catch(() => undefined);
  }, [shownPoll, room?.banner_pos, room?.banner_x, room?.banner_y, room?.accent, joined]);

  async function openPoll(body: { question: string; options: string[] }) {
    if (!room) return;
    const options = body.options.map((o) => o.trim()).filter(Boolean);
    if (!body.question.trim() || options.length < 2) { setError('a poll needs a question and at least two answers'); return; }
    try {
      const p = await api.openPoll(room.id, { question: body.question.trim(), options });
      setPolls((prev) => [p, ...prev.map((x) => ({ ...x, status: 'closed' as const }))]);
      setPollForm({ question: '', options: ['', ''] }); setPollOnScreen(p.id);
    } catch (e) { setError((e as Error).message); }
  }

  async function closePoll(p: RoomPoll) {
    if (!room) return;
    try { const c = await api.closePoll(room.id, p.id); setPolls((prev) => prev.map((x) => (x.id === c.id ? c : x))); }
    catch (e) { setError((e as Error).message); }
  }

  /** D20: the run of show — drafted by the director, kept as edited, the clock started by the host or at on air. */
  async function draftPlan() {
    if (!room) return;
    setDrafting(true);
    try { const plan = await api.writePlan(room.id, Math.max(5, Math.min(240, Math.round(Number(planMinutes)) || 30))); setPlanForm(plan); setRooms((prev) => prev.map((x) => (x.id === room.id ? { ...x, plan } : x))); }
    catch (e) { setError((e as Error).message); }
    finally { setDrafting(false); }
  }
  async function savePlan(plan: PlanSegment[]) {
    const whole = plan.filter((s) => s.title.trim() && s.minutes >= 1).map((s) => ({ title: s.title.trim(), minutes: Math.round(s.minutes), ...(s.note?.trim() ? { note: s.note.trim() } : {}) }));
    if (whole.length !== plan.length) return;
    await saveRoom({ plan: whole.length ? whole : null });
  }
  const clock = room ? showClock(room.plan ?? [], room.plan_started_at ?? (room.egress_id ? room.on_air_at : null), now) : null;
  // the segment the show is in goes to the stage as a small tag, once per change
  const nowSent = useRef<string>('');
  useEffect(() => {
    const title = clock && room?.plan ? (room.plan[clock.index]?.title ?? '') : '';
    if (title === nowSent.current || !mediaRoom.current) return;
    nowSent.current = title;
    void sendToRoom(mediaRoom.current, encodeStageMessage({ type: 'now', title })).catch(() => undefined);
  }, [clock?.index, room?.plan, joined]);

  /** D19: off the air, the director debriefs; the next episode starts from it. */
  async function debrief() {
    if (!room) return;
    setDebriefing(true);
    try {
      const d = await api.roomDebrief(room.id);
      setRooms((prev) => prev.map((x) => (x.id === room.id ? { ...x, debrief: d, debriefed_at: new Date().toISOString() } : x)));
      setDebriefOpen(true);
    } catch (e) { setError((e as Error).message); }
    finally { setDebriefing(false); }
  }

  async function startNextEpisode() {
    const d = room?.debrief; if (!room || !d?.nextEpisode.title) return;
    setBusy(true);
    try {
      const card = await api.createCard({ title: d.nextEpisode.title, brandId: brand?.id ?? null, topic: d.nextEpisode.angle.slice(0, 200) });
      await api.saveBrief(card.id, [d.nextEpisode.angle, '', ...d.asked.slice(0, 3).map((q) => `- ${q}`)].join('\n')).catch(() => undefined);
      onOpenCard?.(card.id);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  /** D18: the director reads the queue for the host. */
  async function askDirector() {
    if (!room) return;
    setReading(true);
    try { setRead(await api.readChat(room.id)); }
    catch (e) { setError((e as Error).message); }
    finally { setReading(false); }
  }

  async function putLine(text: string, name = 'Host', guestId: string | null = null) {
    if (!room || !text.trim()) return;
    try {
      const c = await api.hostLine(room.id, { name, text: text.trim(), guestId });
      setComments((prev) => [c, ...prev.map((x) => (x.status === 'shown' ? { ...x, status: 'pending' as const } : x))]);
      setLine('');
    } catch (e) { setError((e as Error).message); }
  }

  async function setCommentStatus(c: RoomComment, status: 'shown' | 'hidden' | 'pending') {
    if (!room) return;
    try {
      // one on the screen at a time: showing this one takes the last one down
      if (status === 'shown') await Promise.all(comments.filter((x) => x.status === 'shown' && x.id !== c.id).map((x) => api.setComment(room.id, x.id, 'pending')));
      const next = await api.setComment(room.id, c.id, status);
      setComments((prev) => prev.map((x) => (x.id === next.id ? next : status === 'shown' && x.status === 'shown' ? { ...x, status: 'pending' as const } : x)));
    } catch (e) { setError((e as Error).message); }
  }

  async function create() {
    if (!name.trim()) { setError('give the room a name — the show, the episode'); return; }
    setBusy(true);
    try {
      const r = await api.createRoom({ name: name.trim(), brandId: brand?.id ?? null, channelId: channelId || null });
      setName(''); setChannelId('');
      await load(); setOpen(r.id);
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function move(g: RoomGuest, to: GuestMove) {
    if (!room) return;
    try {
      const next = await api.moveGuest(room.id, g.id, to);
      setGuests((prev) => prev.map((x) => (x.id === next.id ? next : x)).filter((x) => x.status !== 'removed'));
    } catch (e) { setError((e as Error).message); }
  }

  async function setStatus(status: Room['status']) {
    if (!room) return;
    try { const r = await api.updateRoom(room.id, { status }); setRooms((prev) => prev.map((x) => (x.id === r.id ? r : x)).filter((x) => x.status !== 'closed')); if (status === 'closed') setOpen(null); }
    catch (e) { setError((e as Error).message); }
  }

  // G2: the room goes out. A channel that has never been started has no
  // stream key yet; the platform's Go live page has it, and it goes straight
  // to Secret Manager — never into this page's state for longer than the call.
  async function onAir() {
    if (!room) return;
    setBusy(true); setError(null);
    try {
      let r: Room;
      try { r = await api.roomOnAir(room.id); }
      catch (e) {
        const msg = (e as Error).message;
        if (!/stream key/i.test(msg)) throw e;
        const key = window.prompt('The channel has no stream key yet. Paste the one from the platform\'s Go live page:');
        if (!key?.trim()) { setError(msg); return; }
        r = await api.roomOnAir(room.id, key.trim());
      }
      setRooms((prev) => prev.map((x) => (x.id === r.id ? r : x)));
    } catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }
  async function offAir() {
    if (!room) return;
    setBusy(true); setError(null);
    try { const r = await api.roomOffAir(room.id); setRooms((prev) => prev.map((x) => (x.id === r.id ? r : x))); }
    catch (e) { setError((e as Error).message); }
    finally { setBusy(false); }
  }

  async function joinRoomMedia() {
    if (!room) return;
    setBusy(true);
    try {
      const key = await api.roomMedia(room.id);
      if (!key) { setError('no media server is configured'); return; }
      const r = await joinMedia(key, setSeats, { publish: true });
      mediaRoom.current = r; setJoined(true);
      const cam = r.localParticipant.getTrackPublication('camera' as never)?.track;
      if (cam && me.current) cam.attach(me.current);
    } catch (e) { setError(`could not join the room's media: ${(e as Error).message}`); }
    finally { setBusy(false); }
  }
  async function leaveRoomMedia() {
    await mediaRoom.current?.disconnect(); mediaRoom.current = null; setJoined(false); setSeats([]);
  }
  useEffect(() => { void leaveRoomMedia(); /* a different room is a different door */ }, [open]);
  useEffect(() => () => { void mediaRoom.current?.disconnect(); }, []);
  const seatOf = (g: RoomGuest) => seats.find((s) => s.identity === `guest-${g.id}`) ?? null;

  const waiting = guests.filter((g) => g.status === 'waiting' || g.status === 'held');
  const inside = guests.filter((g) => g.status === 'admitted' || g.status === 'on_air' || g.status === 'moderator');

  return (
    <section className="panel">
      <div className="vhead" style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}>Live</h2>
        <span className="sub-dim">a room is a show's door — guests arrive by a link, wait, and you bring them up</span>
        <span style={{ flex: 1 }} />
        {!media && <span className="chip" title="Set LIVEKIT_URL, LIVEKIT_API_KEY and LIVEKIT_API_SECRET on the API to carry camera and sound between everyone.">no media server yet — the door still works</span>}
      </div>
      {error && <div className="err" role="alert">{error}</div>}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBlock: 10 }}>
        <input value={name} onChange={(e) => setName(e.target.value)} placeholder="New room — the show or the episode" style={{ minWidth: 260 }} data-move="live-schedule" />
        <select value={channelId} onChange={(e) => setChannelId(e.target.value)} title="The channel this room goes out on, if any">
          <option value="">no channel</option>
          {channels.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <button className="btn" disabled={busy} onClick={() => void create()}>Open a room</button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(200px, 1fr) minmax(0, 3fr)', gap: 16 }}>
        <div>
          {rooms.length === 0 && <div className="empty">No rooms open.</div>}
          {rooms.map((r) => (
            <div key={r.id} className="r" style={{ padding: '8px 6px', borderTop: '1px solid var(--line)', cursor: 'pointer', background: r.id === open ? 'var(--surface-2, transparent)' : undefined }}
                 onClick={() => setOpen(r.id)}>
              <div><strong>{r.name}</strong></div>
              <div className="sub-dim">{r.status}</div>
            </div>
          ))}
        </div>
        <div>
          {room ? (
            <>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 8 }}>
                <strong>{room.name}</strong>
                <span className="chip">{room.status}</span>
                <code style={{ userSelect: 'all' }}>{link}</code>
                <button className="btn tiny" onClick={() => { void navigator.clipboard?.writeText(link).then(() => { setCopied(true); setTimeout(() => setCopied(false), 1500); }); }}>{copied ? 'copied' : 'copy link'}</button>
                <span style={{ flex: 1 }} />
                {!room.egress_id && media && room.channel_id && (
                  <button className="btn tiny pri" disabled={busy} title="The room goes out to its channel through the media server — everyone in it, in boxes, the host first"
                          onClick={() => void onAir()}>On air → channel</button>
                )}
                {!room.egress_id && (!media || !room.channel_id) && room.status !== 'live' && <button className="btn tiny" onClick={() => void setStatus('live')}>Go live</button>}
                {room.egress_id && <button className="btn tiny" disabled={busy} onClick={() => void offAir()}>Off air</button>}
                {!room.egress_id && room.status === 'live' && <button className="btn tiny" onClick={() => void setStatus('open')}>Off air</button>}
                {room.egress_id && <span className="chip" style={{ background: '#dc2626', color: 'var(--on-accent)' }}>● ON AIR{room.on_air_at ? ` since ${new Date(room.on_air_at).toLocaleTimeString()}` : ''}</span>}
              </div>
              {/* D19: off the air, the debrief — what was asked, what to follow up, the next episode. */}
              {!room.egress_id && room.status !== 'live' && (
                <div data-tool="debrief" style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: 13 }}>After the show</strong>
                    <span className="sub-dim">{room.debriefed_at ? `debriefed ${new Date(room.debriefed_at).toLocaleString()}` : 'the director reads what was asked and said, and writes the follow-up'}</span>
                    <span style={{ flex: 1 }} />
                    {room.debrief && <button className="btn tiny" onClick={() => setDebriefOpen(!debriefOpen)}>{debriefOpen ? 'close' : 'read it'}</button>}
                    <button className="btn tiny" data-tool="debrief-ask" disabled={debriefing} onClick={() => void debrief()}>{debriefing ? 'Writing…' : room.debrief ? 'Debrief again' : 'Debrief with the director'}</button>
                  </div>
                  {debriefOpen && room.debrief && (
                    <div style={{ marginTop: 8, display: 'grid', gap: 8 }}>
                      <p style={{ margin: 0 }}>{room.debrief.summary}</p>
                      {room.debrief.asked.length > 0 && <div><strong style={{ fontSize: 13 }}>They asked</strong><ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>{room.debrief.asked.map((q) => <li key={q}>{q}</li>)}</ul></div>}
                      {room.debrief.followUps.length > 0 && <div><strong style={{ fontSize: 13 }}>Follow up</strong><ul style={{ margin: '2px 0 0', paddingLeft: 18 }}>{room.debrief.followUps.map((f) => <li key={f.title}>{f.title} <em className="sub-dim">— {f.why}</em></li>)}</ul></div>}
                      {room.debrief.thank.length > 0 && <div><strong style={{ fontSize: 13 }}>Thank</strong> <span>{room.debrief.thank.join(', ')}</span></div>}
                      {room.debrief.nextEpisode.title && (
                        <div style={{ borderTop: '1px solid var(--line)', paddingTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                          <div style={{ flex: 1, minWidth: 240 }}><strong style={{ fontSize: 13 }}>Next episode:</strong> {room.debrief.nextEpisode.title}<div className="sub-dim">{room.debrief.nextEpisode.angle}</div></div>
                          <button className="btn tiny pri" data-tool="debrief-next" disabled={busy} onClick={() => void startNextEpisode()}>Start the next episode</button>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
              {/* D20: the run of show — the plan, and the floor manager's clock once it starts. */}
              <details data-tool="run-of-show" open={!!clock || !room.plan?.length} style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px', marginBottom: 10, ...(clock && clock.done ? { borderColor: 'var(--red)' } : {}) }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600, display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <span>Run of show{room.plan?.length ? ` · ${planTotal(room.plan)} min in ${room.plan.length} segments` : ''}</span>
                  {clock && <span className="chip" data-tool="clock-nudge" style={{ background: clock.done ? 'var(--red)' : clock.progress > 0.85 ? 'var(--accent)' : undefined, color: clock.done || clock.progress > 0.85 ? 'var(--on-accent)' : undefined, fontWeight: 600 }}>{mmss(clock.elapsed)} · {clock.nudge}</span>}
                </summary>
                {clock && room.plan && (
                  <div style={{ display: 'grid', gap: 3, marginTop: 8 }} data-tool="clock">
                    {room.plan.map((s, i) => { const on = i === clock.index; const pct = i < clock.index ? 100 : on ? Math.min(100, Math.round(clock.progress * 100)) : 0; return (
                      <div key={i} style={{ position: 'relative', padding: '4px 8px', borderRadius: 4, overflow: 'hidden', fontSize: 13, fontWeight: on ? 700 : 400, background: 'var(--surface-2, transparent)', opacity: i < clock.index ? .6 : 1 }}>
                        <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: on && clock.done ? 'var(--red)' : (room.accent ?? 'var(--accent)'), opacity: .3, transition: 'width 1s linear' }} />
                        <span style={{ position: 'relative', display: 'flex', justifyContent: 'space-between', gap: 8 }}><span>{on ? '▶ ' : ''}{s.title}{s.note && on ? <em className="sub-dim" style={{ fontWeight: 400 }}> — {s.note}</em> : null}</span><span>{on ? `${mmss(clock.into)} / ` : ''}{s.minutes} min</span></span>
                      </div>); })}
                  </div>
                )}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginTop: 8 }}>
                  {!room.plan_started_at
                    ? <button className="btn tiny pri" data-tool="start-clock" disabled={!room.plan?.length} title="The clock starts now; at On air it starts by itself" onClick={() => void saveRoom({ planStartedAt: new Date().toISOString() })}>Start the clock</button>
                    : <button className="btn tiny" onClick={() => void saveRoom({ planStartedAt: null })}>Reset the clock</button>}
                  <span style={{ flex: 1 }} />
                  <label className="sub-dim" style={{ display: 'flex', gap: 4, alignItems: 'center' }}>minutes <input type="number" min={5} max={240} value={planMinutes} style={{ width: 64 }} onChange={(e) => setPlanMinutes(e.target.value)} /></label>
                  <button className="btn tiny" data-tool="draft-plan" disabled={drafting} title="The director plans the segments from the show, the rules, last time's questions and the minutes" onClick={() => void draftPlan()}>{drafting ? 'Planning…' : room.plan?.length ? 'Draft again with the director' : 'Draft with the director'}</button>
                </div>
                <div style={{ display: 'grid', gap: 4, marginTop: 8 }}>
                  {planForm.map((s, i) => (
                    <div key={i} style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                      <input value={s.title} maxLength={60} placeholder="Segment" style={{ width: 200 }} onChange={(e) => setPlanForm(planForm.map((x, j) => (j === i ? { ...x, title: e.target.value } : x)))} onBlur={() => void savePlan(planForm)} />
                      <input type="number" min={1} max={120} value={s.minutes} style={{ width: 60 }} onChange={(e) => setPlanForm(planForm.map((x, j) => (j === i ? { ...x, minutes: Number(e.target.value) } : x)))} onBlur={() => void savePlan(planForm)} />
                      <input value={s.note ?? ''} maxLength={200} placeholder="A note for you — the beat, the question, the ask" style={{ flex: 1, minWidth: 180 }} onChange={(e) => setPlanForm(planForm.map((x, j) => (j === i ? { ...x, note: e.target.value } : x)))} onBlur={() => void savePlan(planForm)} />
                      <button className="btn tiny" onClick={() => { const p = planForm.filter((_, j) => j !== i); setPlanForm(p); void savePlan(p); }}>remove</button>
                    </div>
                  ))}
                  {planForm.length < 20 && <button className="btn tiny" style={{ justifySelf: 'start' }} onClick={() => setPlanForm([...planForm, { title: '', minutes: 5 }])}>+ segment</button>}
                </div>
              </details>
              {/* D13: the pre-flight — what a floor manager checks before the red light, each line with its fix. */}
              <ul className="preflight" data-tool="live-preflight" style={{ marginTop: 8 }}>
                {livePreflight({
                  room: { status: room.status, channel_id: room.channel_id, egress_id: room.egress_id },
                  channel: channels.find((c) => c.id === room.channel_id) ?? null,
                  media, joined, guests,
                }).map((c, i) => (
                  <li key={i} className={c.ok ? 'ok' : 'warning'}>
                    <span className="mark">{c.ok ? '✓' : 'Fix'}</span>
                    <span>{c.says}{c.fix && !c.ok && <em> — {c.fix}</em>}</span>
                  </li>
                ))}
              </ul>
              <LiveFromTheDoor reminders={room.reminders ?? 0} plan={room.plan ?? []}
                               onAddSegment={async (seg) => { const p = [...(room.plan ?? []), seg].slice(0, 20); setPlanForm(p); await savePlan(p); }} />
              <div style={{ display: 'none' }}>
                <button className="btn tiny" onClick={() => void setStatus('closed')}>Close the door</button>
              </div>
              {/* G8/G11: the control room — what the door says, the house rules, where tips go, what a super chat is, where its banner sits. */}
              <details className="control-room" data-tool="control-room" style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px', marginBottom: 10 }}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Control room — the door, the rules, tips and super chats</summary>
                <RoomLook room={room} brandId={brand?.id ?? null} onSave={(lookId) => void saveRoom({ lookId })} />
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 8 }}>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>What the door says about the show
                    <textarea rows={2} maxLength={300} value={form.description} placeholder="A short description — what this live is, who is on" onChange={(e) => setForm({ ...form, description: e.target.value })} onBlur={() => void saveRoom({ description: form.description })} /></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>House rules (moderators keep to these)
                    <textarea rows={2} maxLength={1000} value={form.rules} placeholder={'Be kind. Questions on the topic. No links. One warning, then out.'} onChange={(e) => setForm({ ...form, rules: e.target.value })} onBlur={() => void saveRoom({ rules: form.rules })} /></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>The door's picture
                    <select value={room.picture_id ?? ''} onChange={(e) => void saveRoom({ pictureId: e.target.value || null })}>
                      <option value="">none — the name and description</option>
                      {images.map((a) => <option key={a.id} value={a.id}>{assetName(a)}</option>)}
                    </select></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>The door's colour
                    <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                      <input type="color" value={form.accent || brand?.accent_hex || '#e0b100'} onChange={(e) => { setForm({ ...form, accent: e.target.value }); void saveRoom({ accent: e.target.value }); }} />
                      <button className="btn tiny" onClick={() => { setForm({ ...form, accent: '' }); void saveRoom({ accent: null }); }}>the show's</button>
                    </span></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>Where tips go (your PayPal.me or Cash App link)
                    <input value={form.donateUrl} placeholder="https://paypal.me/yourname or https://cash.app/$yourname" onChange={(e) => setForm({ ...form, donateUrl: e.target.value })}
                           onBlur={() => { if (!form.donateUrl || /^https:\/\//.test(form.donateUrl)) void saveRoom({ donateUrl: form.donateUrl || null }); else setError('the tip link must start with https://'); }} /></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>The tip button says
                    <input value={form.donateLabel} maxLength={40} placeholder="Tip the show" onChange={(e) => setForm({ ...form, donateLabel: e.target.value })} onBlur={() => void saveRoom({ donateLabel: form.donateLabel || null })} /></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>A super chat is a tip of at least ($)
                    <input type="number" min={0} max={1000} step={1} value={form.superchat} onChange={(e) => setForm({ ...form, superchat: e.target.value })}
                           onBlur={() => { const n = Math.round(Number(form.superchat) * 100); if (Number.isFinite(n) && n >= 0 && n <= 100000) void saveRoom({ superchatMin: n }); }} /></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>The comment banner is
                    <select value={room.banner_style ?? 'card'} data-tool="banner-style" onChange={(e) => void saveRoom({ bannerStyle: e.target.value as BannerStyle })}>
                      {BANNER_STYLES.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </select></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>The comment banner sits
                    <select value={roomXY(room) ? 'dragged' : room.banner_pos ?? 'bottom'} onChange={(e) => { if (e.target.value !== 'dragged') void saveRoom({ bannerPos: e.target.value as BannerPos, bannerXY: null }); }}>
                      {roomXY(room) && <option value="dragged">where you dragged it</option>}
                      {BANNER_POSITIONS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                    </select></label>
                </div>
                {/* G15: the live's own page — a welcome, the next live, light or dark, the links that matter. The show's logo comes from the brand kit. */}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 10, marginTop: 10 }} data-tool="door-page">
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>A welcome line above the name
                    <input value={form.welcome} maxLength={120} placeholder="Welcome to the garden — grab a coffee" onChange={(e) => setForm({ ...form, welcome: e.target.value })} onBlur={() => void saveRoom({ welcome: form.welcome || null })} /></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>The next live is at
                    <span style={{ display: 'flex', gap: 6 }}>
                      <input type="datetime-local" value={form.nextAt} onChange={(e) => setForm({ ...form, nextAt: e.target.value })}
                             onBlur={() => void saveRoom({ nextAt: form.nextAt ? new Date(form.nextAt).toISOString() : null })} />
                      {form.nextAt && <button className="btn tiny" onClick={() => { setForm({ ...form, nextAt: '' }); void saveRoom({ nextAt: null }); }}>clear</button>}
                    </span></label>
                  <label style={{ display: 'grid', gap: 2, fontSize: 13 }}>The page is
                    <select value={room.theme ?? 'dark'} onChange={(e) => void saveRoom({ theme: e.target.value as 'dark' | 'light' })}>
                      <option value="dark">dark</option><option value="light">light</option>
                    </select></label>
                  <div style={{ display: 'grid', gap: 4, fontSize: 13, gridColumn: '1 / -1' }}>
                    <span>Links on the page (up to six — your channel, your shop, your socials)</span>
                    {form.links.map((l, i) => (
                      <span key={i} style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <input value={l.label} maxLength={40} placeholder="Label" style={{ width: 140 }} onChange={(e) => setForm({ ...form, links: form.links.map((x, j) => (j === i ? { ...x, label: e.target.value } : x)) })} onBlur={() => void saveLinks(form.links)} />
                        <input value={l.url} placeholder="https://…" style={{ flex: 1, minWidth: 200 }} onChange={(e) => setForm({ ...form, links: form.links.map((x, j) => (j === i ? { ...x, url: e.target.value } : x)) })} onBlur={() => void saveLinks(form.links)} />
                        <button className="btn tiny" onClick={() => { const links = form.links.filter((_, j) => j !== i); setForm({ ...form, links }); void saveLinks(links); }}>remove</button>
                      </span>
                    ))}
                    {form.links.length < 6 && <button className="btn tiny" style={{ justifySelf: 'start' }} onClick={() => setForm({ ...form, links: [...form.links, { label: '', url: '' }] })}>+ link</button>}
                  </div>
                </div>
                {/* G14: drag the banner on a small stage; it lands there on the real one. */}
                <div ref={miniStage} data-tool="banner-stage" onPointerDown={dragBanner} title="Drag the banner where you want it on the screen"
                     style={{ position: 'relative', aspectRatio: '16 / 9', maxWidth: 480, background: '#101418', borderRadius: 8, marginTop: 10, overflow: 'hidden', cursor: 'grab', touchAction: 'none', userSelect: 'none' }}>
                  <span style={{ position: 'absolute', left: '2.5%', top: '2.8%', width: 4, height: '2.8%', background: room.accent ?? 'var(--accent)' }} />
                  <span style={{ position: 'absolute', left: '4%', top: '2.8%', color: 'white', fontSize: 9, fontWeight: 700, opacity: .8 }}>{brand?.name ?? room.name}</span>
                  {(() => {
                    const b = bannerBox({ pos: room.banner_pos ?? 'bottom', xy: dragXY ?? roomXY(room) });
                    const pct = (n: number | undefined, of: number) => (n === undefined ? undefined : `${(n / of) * 100}%`);
                    return (
                      <div style={{ position: 'absolute', left: pct(b.left, 1920), right: pct(b.right, 1920), top: pct(b.top, 1080), bottom: pct(b.bottom, 1080), width: `${(b.width / 1920) * 100}%`,
                                    background: 'rgba(12,14,18,.9)', borderLeft: `3px solid ${room.accent ?? '#e0b100'}`, borderRadius: 3, padding: '3px 6px', color: 'white', fontSize: 9, lineHeight: 1.2, pointerEvents: 'none' }}>
                        <strong>{shownNow?.name ?? 'A name'}</strong> {shownNow?.text ?? 'The comment on the screen sits here'}
                      </div>
                    );
                  })()}
                </div>
                <p className="sub-dim" style={{ margin: '8px 0 0' }}>Tips go straight to your own link; the platform holds no money. A guest says what they tipped, and you or a moderator decide whether it goes on the screen. Saved when you leave a field.</p>
              </details>
              {media && (
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 10, flexWrap: 'wrap' }}>
                  {!joined
                    ? <button className="btn" disabled={busy} onClick={() => void joinRoomMedia()}>Join the room (camera + mic)</button>
                    : <button className="btn" onClick={() => void leaveRoomMedia()}>Leave the room's media</button>}
                  {joined && (
                    <button className={`btn${screenOn ? ' pri' : ''}`} data-tool="share-screen" title="Spyglass on the live: your screen goes out large, the faces beside it — a demo, a slide, a browser"
                            onClick={() => { if (!mediaRoom.current) return; void shareScreen(mediaRoom.current, !screenOn).then((on) => setScreenOn(on)).catch((e) => setError((e as Error).message)); }}>
                      {screenOn ? 'Stop sharing my screen' : 'Share my screen'}
                    </button>
                  )}
                  <div style={{ width: 160, aspectRatio: '16 / 9', background: '#000', borderRadius: 6, overflow: 'hidden' }}>
                    <video ref={me} muted playsInline style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'scaleX(-1)' }} />
                  </div>
                  <span className="sub-dim">{joined ? 'You are in the room; admitted guests can see and hear you.' : 'Join to see and hear the guests you admit.'}</span>
                </div>
              )}
              <div className="sub-dim" style={{ marginBottom: 10 }}>Send the link to a guest. They give a name, wait here, and you bring them up.</div>

              <h4 style={{ margin: '8px 0 4px' }}>At the door ({waiting.length})</h4>
              {waiting.length === 0 && <div className="sub-dim">Nobody waiting.</div>}
              {waiting.map((g) => (
                <div key={g.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderTop: '1px solid var(--line)' }}>
                  <span className="chip" style={{ background: TONE[g.status], color: 'white' }}>{WORD[g.status]}</span>
                  <GuestWho g={g} />
                  <span style={{ flex: 1 }} />
                  <button className="btn tiny" onClick={() => void move(g, 'admit')}>Admit</button>
                  {g.status !== 'held' && <button className="btn tiny" onClick={() => void move(g, 'hold')}>Hold</button>}
                  <button className="btn tiny" onClick={() => void move(g, 'remove')}>Turn away</button>
                </div>
              ))}

              <h4 style={{ margin: '14px 0 4px' }}>In the room ({inside.length})</h4>
              {inside.length === 0 && <div className="sub-dim">Nobody in yet.</div>}
              {inside.map((g) => (
                <div key={g.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '6px 0', borderTop: '1px solid var(--line)' }}>
                  <div style={{ width: 120, aspectRatio: '16 / 9', background: '#111', borderRadius: 6, overflow: 'hidden', flex: 'none' }}
                       ref={(box) => mount(box, seatOf(g)?.element ?? null)} title={seatOf(g) ? 'live' : 'no picture yet'} />
                  <span className="chip" style={{ background: TONE[g.status], color: 'white' }}>{WORD[g.status]}</span>
                  <GuestWho g={g} />
                  <span style={{ flex: 1 }} />
                  {g.status !== 'on_air' && <button className="btn tiny" onClick={() => void move(g, 'on-air')}>Bring up</button>}
                  {g.status === 'on_air' && <button className="btn tiny" onClick={() => void move(g, 'admit')}>Send back</button>}
                  {g.question && <button className="btn tiny" title="Their question, on the screen under them" onClick={() => void putLine(g.question ?? '', g.name, g.id)}>Their question on screen</button>}
                  {g.status !== 'moderator'
                    ? <button className="btn tiny" title="A moderator keeps the door and the comments from their own page — admits, holds, removes, shows and hides" onClick={() => void move(g, 'moderator')}>Make moderator</button>
                    : <button className="btn tiny" onClick={() => void move(g, 'demote')}>Demote</button>}
                  <button className="btn tiny" onClick={() => void move(g, 'hold')}>Hold</button>
                  <button className="btn tiny" onClick={() => void move(g, 'remove')}>Remove</button>
                </div>
              ))}

              {Object.keys(reactions).length > 0 && (
                <div data-tool="reactions-now" style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 10, fontSize: 18 }}>
                  <span className="sub-dim" style={{ fontSize: 12 }}>The room, right now:</span>
                  {Object.entries(reactions).sort((a, b) => b[1] - a[1]).map(([e, n]) => <span key={e}>{e} <small>{n}</small></span>)}
                </div>
              )}
              {/* G16: polls — ask, watch the bars, put it on the screen, close it. */}
              <details data-tool="polls" style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px', marginTop: 14 }} open={polls.some((p) => p.status === 'open')}>
                <summary style={{ cursor: 'pointer', fontWeight: 600 }}>Polls{polls.some((p) => p.status === 'open') ? ' — one open' : ''}</summary>
                <div style={{ display: 'grid', gap: 6, marginTop: 8 }}>
                  <input value={pollForm.question} maxLength={200} placeholder="The question — Mulch or no mulch?" onChange={(e) => setPollForm({ ...pollForm, question: e.target.value })} />
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {pollForm.options.map((o, i) => <input key={i} value={o} maxLength={60} placeholder={`Answer ${i + 1}`} style={{ width: 150 }} onChange={(e) => setPollForm({ ...pollForm, options: pollForm.options.map((x, j) => (j === i ? e.target.value : x)) })} />)}
                    {pollForm.options.length < 5 && <button className="btn tiny" onClick={() => setPollForm({ ...pollForm, options: [...pollForm.options, ''] })}>+ answer</button>}
                    <button className="btn tiny pri" data-tool="open-poll" onClick={() => void openPoll(pollForm)}>Ask the room</button>
                  </div>
                </div>
                {polls.map((p) => (
                  <div key={p.id} style={{ borderTop: '1px solid var(--line)', padding: '6px 0', opacity: p.status === 'open' ? 1 : .7 }}>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                      <strong>{p.question}</strong>
                      <span className="chip">{p.status === 'open' ? 'open' : 'closed'} · {p.tallies.reduce((a, b) => a + b, 0)} votes</span>
                      <span style={{ flex: 1 }} />
                      {pollOnScreen === p.id
                        ? <button className="btn tiny" onClick={() => setPollOnScreen(null)}>Take it down</button>
                        : <button className="btn tiny pri" disabled={!joined && media} title={!joined && media ? 'join the room to put it on the stage' : ''} onClick={() => setPollOnScreen(p.id)}>On screen</button>}
                      {p.status === 'open' && <button className="btn tiny" onClick={() => void closePoll(p)}>Close</button>}
                    </div>
                    <div style={{ display: 'grid', gap: 3, marginTop: 4 }}>
                      {p.options.map((o, i) => { const pct = pollShares(p.tallies)[i] ?? 0; return (
                        <div key={i} style={{ position: 'relative', fontSize: 12, padding: '2px 6px', borderRadius: 4, overflow: 'hidden', background: 'var(--surface-2, transparent)' }}>
                          <span style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: room.accent ?? 'var(--accent)', opacity: .3, transition: 'width .4s' }} />
                          <span style={{ position: 'relative', display: 'flex', justifyContent: 'space-between' }}><span>{o}</span><span>{p.tallies[i] ?? 0} · {pct}%</span></span>
                        </div>); })}
                    </div>
                  </div>
                ))}
              </details>

              {/* G10/G12: the comments — waiting first, super chats at the top; one on the screen at a time. */}
              <h4 style={{ margin: '14px 0 4px', display: 'flex', gap: 8, alignItems: 'center' }} data-tool="comment-queue">Comments ({comments.filter((c) => c.status === 'pending').length} waiting)
                <span style={{ flex: 1 }} />
                {shownNow && <button className="btn tiny" onClick={() => void setCommentStatus(shownNow, 'pending')}>Clear the screen</button>}
                <button className="btn tiny" data-tool="ask-director-chat" disabled={reading || comments.every((c) => c.status !== 'pending')} title="The director reads what is waiting: three for the screen, the themes, a line for you, what breaks the rules" onClick={() => void askDirector()}>{reading ? 'Reading…' : 'Ask the director'}</button>
                {!joined && media && <span className="sub-dim" style={{ fontWeight: 400 }}>join the room to put a comment on the stage</span>}
              </h4>
              {read && (
                <div className="director-read" data-tool="director-read" style={{ border: '1px solid var(--line)', borderRadius: 8, padding: '6px 10px', marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                    <strong style={{ fontSize: 13 }}>The director read the chat</strong>
                    {read.themes.map((t) => <span key={t} className="chip">{t}</span>)}
                    <span style={{ flex: 1 }} />
                    <button className="btn tiny" onClick={() => setRead(null)}>close</button>
                  </div>
                  {read.picks.length === 0 && read.watch.length === 0 && !read.line && <div className="sub-dim">Nothing waiting worth the screen.</div>}
                  {read.picks.map((p) => { const c = comments.find((x) => x.id === p.id); return c ? (
                    <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '1px solid var(--line)' }}>
                      <strong>{c.name}</strong><span style={{ flex: 1 }}>{c.text}</span><em className="sub-dim">{p.why}</em>
                      {c.status !== 'shown' && <button className="btn tiny pri" onClick={() => void setCommentStatus(c, 'shown')}>On screen</button>}
                    </div>) : null; })}
                  {read.watch.map((p) => { const c = comments.find((x) => x.id === p.id); return c ? (
                    <div key={p.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '1px solid var(--line)', opacity: .8 }}>
                      <span className="chip" style={{ background: 'var(--red)', color: 'var(--on-accent)' }}>watch</span>
                      <strong>{c.name}</strong><span style={{ flex: 1 }}>{c.text}</span><em className="sub-dim">{p.why}</em>
                      {c.status !== 'hidden' && <button className="btn tiny" onClick={() => void setCommentStatus(c, 'hidden')}>Hide</button>}
                    </div>) : null; })}
                  {read.line && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '1px solid var(--line)' }}>
                      <span className="sub-dim">Say next:</span><span style={{ flex: 1 }}>{read.line}</span>
                      <button className="btn tiny" onClick={() => setLine(read.line)}>Use this line</button>
                    </div>
                  )}
                  {read.poll && (
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '4px 0', borderTop: '1px solid var(--line)' }} data-tool="director-poll">
                      <span className="sub-dim">Poll:</span><span style={{ flex: 1 }}>{read.poll.question} <em className="sub-dim">— {read.poll.options.join(' / ')}</em></span>
                      <button className="btn tiny pri" onClick={() => void openPoll(read.poll!)}>Run this poll</button>
                    </div>
                  )}
                </div>
              )}
              <div style={{ display: 'flex', gap: 6, marginBottom: 6 }} data-tool="host-line">
                <input value={line} maxLength={300} placeholder="A line of your own on the screen — a question, a poll, a note" onChange={(e) => setLine(e.target.value)}
                       onKeyDown={(e) => { if (e.key === 'Enter') void putLine(line); }} style={{ flex: 1, minWidth: 200 }} />
                <button className="btn tiny pri" disabled={!line.trim()} onClick={() => void putLine(line)}>On screen</button>
              </div>
              {comments.length === 0 && <div className="sub-dim">No comments yet. Anyone at the door can leave one.</div>}
              <div style={{ maxHeight: 320, overflowY: 'auto' }}>
                {queueOrder(comments, room.superchat_min ?? 500).map((c) => (
                  <div key={c.id} style={{ display: 'flex', gap: 8, alignItems: 'center', padding: '5px 0', borderTop: '1px solid var(--line)', opacity: c.status === 'hidden' ? .55 : 1 }}>
                    {c.amount_cents > 0 && <span className="chip" style={{ background: isSuperChat(c, room.superchat_min ?? 500) ? (room.accent ?? 'var(--accent)') : undefined, fontWeight: 700 }} title={isSuperChat(c, room.superchat_min ?? 500) ? 'a super chat, as they told us' : 'a tip, as they told us'}>{money(c.amount_cents)}</span>}
                    <strong>{c.name}</strong>
                    <span style={{ flex: 1, minWidth: 0 }}>{c.text}</span>
                    {c.status === 'shown' && <span className="chip" style={{ background: '#dc2626', color: 'var(--on-accent)' }}>on screen</span>}
                    {c.status !== 'shown' && <button className="btn tiny pri" onClick={() => void setCommentStatus(c, 'shown')}>On screen</button>}
                    {c.status !== 'hidden' && <button className="btn tiny" onClick={() => void setCommentStatus(c, 'hidden')}>Hide</button>}
                  </div>
                ))}
              </div>
            </>
          ) : <div className="empty">Open a room to get its link.</div>}
        </div>
      </div>
    </section>
  );
}

/** A timestamp as a datetime-local input wants it: local time, to the minute. */
function localInput(iso: string): string {
  const d = new Date(iso); if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
}

/** G14: the banner's dragged place, as the API keeps it (numeric comes back as a string). */
function roomXY(r: Room): [number, number] | null {
  if (r.banner_x == null || r.banner_y == null) return null;
  const x = Number(r.banner_x), y = Number(r.banner_y);
  return Number.isFinite(x) && Number.isFinite(y) ? [x, y] : null;
}

/** G7: who a guest is, as they set it on the door page — with their picture and their question. */
function GuestWho({ g }: { g: RoomGuest }) {
  const line = [g.role, g.location].filter(Boolean).join(' · ');
  return (
    <span style={{ display: 'flex', gap: 8, alignItems: 'center', minWidth: 0 }}>
      {g.avatar
        ? <img src={g.avatar} alt="" width={28} height={28} style={{ borderRadius: '50%', objectFit: 'cover', flex: 'none' }} />
        : <span style={{ width: 28, height: 28, borderRadius: '50%', display: 'grid', placeItems: 'center', background: `hsl(${g.hue ?? 210} 55% 45%)`, color: 'white', fontSize: 11, fontWeight: 700, flex: 'none' }}>{g.name.trim().split(/\s+/).slice(0, 2).map((w) => w[0]?.toUpperCase() ?? '').join('')}</span>}
      <span style={{ display: 'grid', lineHeight: 1.2 }}>
        <span>{g.name}{g.pronouns ? <span className="sub-dim"> ({g.pronouns})</span> : null}{g.handle ? <span className="sub-dim"> {g.handle.startsWith('@') ? g.handle : `@${g.handle}`}</span> : null}{g.camera_off ? <span className="sub-dim" title="Prefers their picture to their camera"> · picture</span> : null}</span>
        {line && <span className="sub-dim" style={{ fontSize: 12 }}>{line}</span>}
        {g.question && <span style={{ fontSize: 12 }} title="Their question for you">❝ {g.question}</span>}
      </span>
    </span>
  );
}
